/**
 * App-Shell — Design 10a (Claude-Design-Brief, Turn 10):
 * - Konto-Menü links unten (Person): Klick auf den Namen öffnet ein Menü nach oben mit
 *   Profil, Benachrichtigungen, Darstellung (Hell/Dunkel/System), Betriebs-Einstellungen,
 *   Hilfe & Support und Abmelden.
 * - Betrieb-Umschalter als eigene Karte darüber (nur bei mehreren Mitgliedschaften klickbar).
 * - Topbar rechts: nur noch die Glocke — das redundante Avatar-„JB" ist entfernt (zeigte
 *   dieselbe Person wie links unten). Theme-Umschaltung lebt jetzt im Konto-Menü; auf Mobile
 *   (Sidebar versteckt) bleibt ein einfacher Hell/Dunkel-Schalter in der Topbar.
 * Dunkelbraune Sidebar (#4A2511), Akzent-Orange aktiv. Labels deutsch (deutsches Produkt).
 *
 * Phase H (H1, UX-Layer): Das Layout ist CSS-Variablen-getrieben (`--nav-w`). Dadurch lässt sich
 * das Menü zu einer schmalen Icon-Leiste (Rail) einklappen und per Ziehgriff in der Breite
 * verstellen. Der Zustand (Breite + eingeklappt) wird in localStorage gehalten und VOR dem ersten
 * Paint angewandt. Mobil (unter lg) bleibt alles wie bisher: Off-Canvas-Copilot + Bottom-Nav,
 * keine Rails/Resizer.
 *
 * Redesign 28.08.2026: der Copilot ist kein angedockter, den Hauptinhalt verdrängender
 * Spalten-Kollege mehr, sondern ein frei schwebendes Fenster (siehe CopilotPanel +
 * useFloatingWindow) — Topbar/Main reservieren ihm deshalb keinen Platz mehr; nur der Ziehgriff
 * zur Breitenverstellung ist entfallen, die Größe kommt jetzt vom Fenster selbst. Der Drawer-
 * Ausweich-Mechanismus, der bis 01.09.2026 dafür trotzdem noch blind rechts Platz reservierte,
 * ist auf Antoines Wunsch ("Solche Fenster müssen immer rechts bündig sein") an diesem Tag
 * zunächst vollständig entfernt worden. Ein Review-Fund direkt danach zeigte: der Copilot hat
 * weiterhin einen echten "Fest verankern"-Modus (`pinnedOpen` unten), der laut `PINNED_W` echt
 * eine 380px-Spalte verdrängt — dafür reserviert der Drawer seitdem wieder gezielt Platz. Details
 * im Kommentar an `Drawer` in ui/components.tsx.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUser } from "@stackframe/react";
import { AppErrorBoundary } from "./ErrorBoundary";
import { useApi } from "../hooks/useApi";
import { currentMembership } from "../lib/membership";
import type { Me, Inquiry } from "./types";
import { Button, Icon, Logo } from "./components";
import { ROLE_LABEL } from "./format";
import { useTheme, THEME_OPTS } from "./theme";
import { aktiverTitel } from "./navigation";
import { useSeitenKontext } from "../lib/rueckweg";
import { SeitenNavigation, MobileNavigation, MobileNavDrawer } from "./NavListe";
import { zaehleWartetAufDich } from "./AnfragenGruppen";
import { HelpPanel } from "./HelpPanel";
import { GlobalSearch } from "./GlobalSearch";
import { GLOBALE_SUCHE_EREIGNIS } from "../lib/globale-suche";
import { NotificationsBell } from "./NotificationsBell";
import { UndoStack } from "./UndoStack";
import { CopilotPanel } from "./CopilotPanel";
import { useCopilot, useContentRefetch } from "./CopilotProvider";
import { PINNED_W } from "../hooks/useFloatingWindow";
import { useReferenceProgress } from "./ReferenceProgressProvider";
import { ImpersonationBanner } from "./ImpersonationBanner";

/* ─────────────────────────────────────────────────────────────────────────────
   Phase H1 — Layout-Chrome: einklappbares Menü (Rail) + verstellbare Spaltenbreiten.
   Quelle der Wahrheit sind die CSS-Variablen `--nav-w` / `--copilot-w`; React hält die
   committeten Werte (für aria-valuenow + Persistenz). Während eines Zugs schreiben wir NUR
   die CSS-Variable (kein Re-Render pro pointermove) und committen erst bei pointerup.
   ───────────────────────────────────────────────────────────────────────────── */
const NAV_DEFAULT = 240;
const NAV_MIN = 200;
const NAV_MAX = 360;
const NAV_RAIL = 64;
// Beim Ziehen (nicht bei Pfeiltasten) darf der Griff bis zur Rail-/Null-Breite schrumpfen; wird
// unterhalb dieser Schwelle losgelassen, klappt die Spalte komplett ein/zu statt auf einer sehr
// schmalen Zwischenbreite zu committen ("so klein ziehen wie eingeklappt").
const NAV_COLLAPSE_SNAP = 140;
// copilotW wird nur noch aus dem gespeicherten Layout gelesen und persistiert — der einzige
// Verbraucher (der inzwischen entfernte Drawer-Ausweich-Mechanismus, s. Kommentar oben) ist am
// 01.09.2026 vollstaendig entfernt worden. Kein Ziehgriff mehr dafuer, daher kein
// COPILOT_COLLAPSE_SNAP mehr noetig.
const COPILOT_DEFAULT = 380;
const COPILOT_MIN = 320;
const COPILOT_MAX = 560;
const LAYOUT_KEY = "maestro.layout.v1";

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

interface StoredLayout {
  navW: number;
  copilotW: number;
  collapsed: boolean;
}

/** Gespeichertes Layout lesen und robust klammern; bei fehlenden/kaputten Werten sinnvolle Defaults. */
function readStoredLayout(): StoredLayout {
  const fallback: StoredLayout = { navW: NAV_DEFAULT, copilotW: COPILOT_DEFAULT, collapsed: false };
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(LAYOUT_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<StoredLayout>;
    return {
      navW: clamp(typeof parsed.navW === "number" ? parsed.navW : NAV_DEFAULT, NAV_MIN, NAV_MAX),
      copilotW: clamp(typeof parsed.copilotW === "number" ? parsed.copilotW : COPILOT_DEFAULT, COPILOT_MIN, COPILOT_MAX),
      collapsed: parsed.collapsed === true,
    };
  } catch {
    return fallback;
  }
}

interface LayoutChrome {
  collapsed: boolean;
  toggleCollapsed: () => void;
  /** true, solange ein Resizer aktiv gezogen wird — schaltet Breiten-/Rand-Übergänge aus. */
  dragging: boolean;
  navW: number;
  startNavDrag: (e: ReactPointerEvent<HTMLElement>) => void;
  onNavKey: (e: ReactKeyboardEvent<HTMLElement>) => void;
  resetNav: () => void;
}

function useLayoutChrome(): LayoutChrome {
  // Lazy-Initialisierung: localStorage genau einmal beim Mount lesen (nicht bei jedem Render).
  const [navW, setNavW] = useState<number>(() => readStoredLayout().navW);
  // Kein Ziehgriff mehr fuer die Breite (siehe Kommentar bei COPILOT_DEFAULT oben) — der Wert
  // wird nur noch gespeichert/gelesen, siehe dort.
  const [copilotW] = useState<number>(() => readStoredLayout().copilotW);
  const [collapsed, setCollapsed] = useState<boolean>(() => readStoredLayout().collapsed);
  const [dragging, setDragging] = useState(false);

  // CSS-Variablen VOR dem ersten Paint setzen — verhindert einen Layout-Sprung, wenn gespeicherte
  // Breiten von den CSS-Defaults (240/380) abweichen. Danach bei jeder Committung spiegeln.
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--nav-w", `${collapsed ? NAV_RAIL : navW}px`);
  }, [navW, collapsed]);

  // Persistenz (per Browser für v1). Fehler (Speicher voll / privater Modus) bleiben folgenlos.
  useEffect(() => {
    try {
      window.localStorage.setItem(LAYOUT_KEY, JSON.stringify({ navW, copilotW, collapsed }));
    } catch {
      /* localStorage nicht verfügbar — das Layout bleibt für diese Sitzung dennoch nutzbar. */
    }
  }, [navW, copilotW, collapsed]);

  const toggleCollapsed = useCallback(() => setCollapsed((v) => !v), []);
  const resetNav = useCallback(() => setNavW(NAV_DEFAULT), []);

  // Gemeinsame Zeiger-Drag-Logik: setPointerCapture + native pointermove/up am Griff. Während des Zugs
  // nur die CSS-Variable schreiben (flüssig, kein Re-Render); bei pointerup den Endwert committen.
  const beginDrag = useCallback(
    (
      e: ReactPointerEvent<HTMLElement>,
      startW: number,
      cssVar: "--nav-w",
      toWidth: (dx: number, from: number) => number,
      commit: (next: number) => void,
    ) => {
      e.preventDefault();
      const el = e.currentTarget;
      const startX = e.clientX;
      const root = document.documentElement;
      let next = startW;
      el.setPointerCapture(e.pointerId);
      setDragging(true);
      const onMove = (ev: PointerEvent) => {
        next = toWidth(ev.clientX - startX, startW);
        root.style.setProperty(cssVar, `${next}px`);
      };
      const onUp = () => {
        el.removeEventListener("pointermove", onMove);
        el.removeEventListener("pointerup", onUp);
        el.removeEventListener("pointercancel", onUp);
        try {
          el.releasePointerCapture(e.pointerId);
        } catch {
          /* Pointer evtl. bereits freigegeben — unkritisch. */
        }
        setDragging(false);
        commit(next);
      };
      el.addEventListener("pointermove", onMove);
      el.addEventListener("pointerup", onUp);
      el.addEventListener("pointercancel", onUp);
    },
    [],
  );

  const startNavDrag = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      // Live darf der Zug bis zur Rail-Breite schrumpfen (echtes "so klein wie eingeklappt"-
      // Gefuehl beim Ziehen); erst beim Loslassen wird entschieden, ob committet oder
      // eingeklappt wird. Die Rail-CSS-Variable stellt sich danach ueber den collapsed-Zweig
      // des useLayoutEffect oben von selbst richtig (der auf `collapsed` reagiert).
      // Eingeklappt startet der Zug an der Rail-Breite, nicht am zuletzt committeten navW -
      // sonst wuerde ein Ziehen aus der schmalen Rail heraus sofort auf die alte Breite
      // springen. So laesst sich das Menü auch per Ziehen wieder aufklappen, nicht nur
      // ueber den Chevron-Button.
      beginDrag(e, collapsed ? NAV_RAIL : navW, "--nav-w", (dx, from) => clamp(from + dx, NAV_RAIL, NAV_MAX), (next) => {
        if (next <= NAV_COLLAPSE_SNAP) {
          setCollapsed(true);
        } else {
          setCollapsed(false);
          setNavW(clamp(next, NAV_MIN, NAV_MAX));
        }
      });
    },
    [beginDrag, navW, collapsed],
  );

  const onNavKey = useCallback((e: ReactKeyboardEvent<HTMLElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const step = (e.shiftKey ? 32 : 16) * (e.key === "ArrowRight" ? 1 : -1);
    setNavW((w) => clamp(w + step, NAV_MIN, NAV_MAX));
  }, []);

  return {
    collapsed,
    toggleCollapsed,
    dragging,
    navW,
    startNavDrag,
    onNavKey,
    resetNav,
  };
}

/** Dünner, greifbarer vertikaler Ziehgriff zwischen zwei Spalten (nur ab lg, Zeiger-Interaktion). */
function Resizer({
  ariaLabel,
  valueNow,
  valueMin,
  valueMax,
  positionStyle,
  edge,
  onPointerDown,
  onKeyDown,
  onDoubleClick,
}: {
  ariaLabel: string;
  valueNow: number;
  valueMin: number;
  valueMax: number;
  positionStyle: CSSProperties;
  /** Auf welcher Seite die Naht liegt — bestimmt die Zentrier-Translation auf die Kante. */
  edge: "left" | "right";
  onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
  onKeyDown: (e: ReactKeyboardEvent<HTMLElement>) => void;
  onDoubleClick: () => void;
}) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      aria-valuenow={valueNow}
      aria-valuemin={valueMin}
      aria-valuemax={valueMax}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      onDoubleClick={onDoubleClick}
      title="Ziehen zum Anpassen · Doppelklick setzt zurück · Pfeiltasten für Feinschritte"
      style={positionStyle}
      className={[
        "group fixed inset-y-0 z-[45] hidden w-2.5 cursor-col-resize touch-none select-none outline-none lg:block",
        edge === "left" ? "-translate-x-1/2" : "translate-x-1/2",
      ].join(" ")}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 bg-transparent transition-colors duration-150 group-hover:bg-primary/40 group-focus-visible:bg-primary/70"
      />
    </div>
  );
}

/** Seitentitel für die Topbar (Design: links steht der aktive Bereichsname, nicht die Wortmarke). */
function useActiveLabel(): string {
  const { pathname } = useLocation();
  const kontext = useSeitenKontext();
  return kontext?.titel ?? aktiverTitel(pathname);
}

/** Schließt ein Popup bei Klick außerhalb oder Escape. Ref auf den umschließenden Container legen. */
function useDismiss<T extends HTMLElement>(open: boolean, onClose: () => void) {
  const ref = useRef<T>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);
  return ref;
}

/** Initialen aus Anzeigename (Vor+Nachname) oder E-Mail. */
function initials(name: string | null | undefined, email: string | null | undefined): string {
  const src = (name ?? "").trim();
  if (src) {
    const parts = src.split(/\s+/);
    const two = (parts[0]?.[0] ?? "") + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "");
    return (two || src[0]!).toUpperCase();
  }
  return (email ?? "?").slice(0, 1).toUpperCase();
}

/** Ein Eintrag im Konto-Menü. */
function MenuItem({
  icon,
  label,
  hint,
  danger,
  onClick,
}: {
  icon: string;
  label: string;
  hint?: string;
  danger?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={[
        "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left font-body-sm text-body-sm transition-colors",
        danger
          ? "font-semibold text-error hover:bg-error/10 dark:text-[#e39b92]"
          : "text-on-background hover:bg-surface-container-high dark:text-inverse-on-surface dark:hover:bg-white/5",
      ].join(" ")}
    >
      <Icon name={icon} className="text-[18px] text-on-surface-variant dark:text-surface-variant" />
      <span className="flex-1 font-medium">{label}</span>
      {hint && <span className="text-[10.5px] text-on-surface-variant dark:text-surface-variant">{hint}</span>}
    </button>
  );
}

/**
 * Dezenter Laufindikator fuer die Referenz-Analyse (Automatik P4.3, LOOP-Entscheidung vom
 * 29.07.2026: "Glocke plus ein dezenter Laufindikator in der Topbar - KEINE von selbst
 * aufpoppenden Einblendungen").
 *
 * Weil er in der Topbar der AppShell haengt, ist er auf JEDER Seite sichtbar - auch im
 * Angebots-Builder, ohne dass dort etwas eingebaut werden muesste. Er verschwindet von selbst,
 * sobald nichts mehr aussteht; danach ruht auch die Abfrage (P4.2). Ein Klick fuehrt dorthin,
 * wo die Dokumente stehen - eine Anzeige ohne Ziel laesst den Betreiber suchen.
 */
function ReferenzLaufIndikator() {
  const { progress, running } = useReferenceProgress();
  const navigate = useNavigate();
  if (!running || !progress) return null;
  // Gezaehlt wird, was noch AUSSTEHT (wartend + in Arbeit) - nicht das schon Fertige. Sonst
  // stiege die Zahl, waehrend die Arbeit weniger wird.
  const offen =
    progress.catering.uploaded + progress.catering.extracting + progress.event.uploaded + progress.event.extracting;
  const text = offen === 1 ? "1 Referenz wird analysiert" : `${offen} Referenzen werden analysiert`;
  return (
    <button
      type="button"
      onClick={() => navigate("/katalog")}
      title={text}
      aria-label={text}
      className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-primary dark:text-surface-variant dark:hover:bg-white/10"
    >
      <Icon name="progress_activity" className="animate-spin text-[18px]" />
      <span className="hidden text-label-md sm:inline">{text}</span>
    </button>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const user = useUser();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [theme, setTheme, resolvedDark] = useTheme();
  const activeLabel = useActiveLabel();
  const { isOpen: copilotOpen, toggle: toggleCopilot, navigateTarget, requestNavigate, floatWin } = useCopilot();
  const { collapsed, toggleCollapsed, dragging, navW, startNavDrag, onNavKey, resetNav } = useLayoutChrome();
  // App-weite Erweiterung 28.08.2026: "Fest verankern" ist eine ECHTE, den Hauptinhalt verdraengende
  // Spalte (Prototyp: body.pinned-open .desk{right:var(--pinned-w)}), kein Ueberlagern wie beim
  // frei schwebenden/angedockten Fenster. Nur wirksam, wenn die Spalte auch tatsaechlich als volle
  // Spalte sichtbar ist (offen, nicht auf die Randleiste eingeklappt) und nur ab lg — auf Mobil ist
  // ohnehin kein Platz fuer eine zweite Spalte (floatWin.active deckt das schon ab).
  const pinnedOpen = floatWin.active && floatWin.mode === "pinned" && !floatWin.collapsed && copilotOpen;
  // Nachbesserung 01.09.2026 (Review-Fund direkt nach dem Ausweich-Rueckbau, s. Kommentar an
  // `Drawer` in ui/components.tsx): spiegelt exakt denselben `pinnedOpen`-Zustand wie die
  // `lg:mr-[380px]`-Klassen unten bei Topbar/Main in eine CSS-Variable, damit der Drawer sich
  // NUR im echten "Fest verankern"-Fall seitlich versetzt statt komplett ueber die angepinnte
  // Spalte zu legen. `useLayoutEffect` (nicht `useEffect`) aus demselben Grund wie beim
  // `--nav-w`-Effekt oben: Wert VOR dem ersten Paint setzen, kein sichtbarer Sprung.
  useLayoutEffect(() => {
    document.documentElement.style.setProperty("--copilot-pinned-w", pinnedOpen ? `${PINNED_W}px` : "0px");
  }, [pinnedOpen]);
  const { data: me, reload: reloadMe } = useApi<Me>("/api/me");
  // Punkt 2 (Settings): useApi hat keinen globalen Cache (eigene Instanz je Aufrufer) - eine
  // Tenant-Umbenennung im Tab "Betrieb" muss der AppShell separat ueber den Copilot-Content-Bus
  // gemeldet werden, damit Topbar/Sidebar-Tenantname sofort mitziehen (siehe settings.tsx).
  useContentRefetch("me", reloadMe);
  const { data: inquiries } = useApi<Inquiry[]>("/api/inquiries");
  // P10.6 (32a, § 12.2 Punkt 2) — eine Zählwahrheit. Hier stand `status === "new"`; damit zählte
  // das Badge etwas anderes als die Kopfzeile der Anfragenliste, und beide widersprachen sich auf
  // demselben Bildschirm. Gezählt wird jetzt „Du bist dran" mit derselben Funktion, die die
  // Kopfzeile speist — nicht „offen", nicht „alle". Fix 03.09.2026 (Domenico via WhatsApp/Antoine:
  // die Glocke ist „dauerhaft an"): `zaehleWartetAufDich` selbst zaehlt seither enger — nur noch
  // Anfragen ohne begonnenes Angebot, siehe `WARTET_MIT_REGEL` in AnfragenGruppen.tsx. Bewusst
  // WEITERHIN dieselbe Funktion wie die Kopfzeile, kein zweiter Zähler fürs Badge (P10.6).
  const wartetAufDich = zaehleWartetAufDich(inquiries);
  // Phase C.2 WP-E (Postfach-Zuordnung): dieselbe Zaehl-Route wie die Bell-Meldung
  // (NotificationsBell.tsx) - EINE Zaehlwahrheit mit dem Tab "Ohne Vorgang n" in posteingang.tsx
  // (beide zaehlen Absender-Gruppen, nicht Einzelnachrichten).
  const { data: inboxZaehler } = useApi<{ ohneVorgang: number; ungelesen: number }>("/api/inbox/zaehler");
  const posteingangOhneVorgang = inboxZaehler?.ohneVorgang ?? 0;
  // Lern-Übersicht (Mockup UXqy6s3qxdztdoVQKAKhi9): „n neu“ am Menüpunkt Gedächtnis. Die Seite
  // meldet ihren Besuch als „gedaechtnis_besuch“ — dann steht der Zähler auf 0.
  const { data: gedaechtnisZaehler, reload: reloadGedaechtnis } = useApi<{ anzahl: number }>("/api/gedaechtnis/neu-anzahl");
  useContentRefetch(["gedaechtnis_besuch", "knowledge"], reloadGedaechtnis);
  const gedaechtnisNeu = gedaechtnisZaehler?.anzahl ?? 0;

  const [accountOpen, setAccountOpen] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  // Nav-Umbau 28.08.2026: Vollbild-Menü fürs Telefon (alle 17 Ziele, s. NavListe.tsx).
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Merge 2026-07-18: Die globale Command-Palette (main, ⌘K) ersetzt die aeltere
  // A-05-Wissenssuche des Topbar-Felds — eine Suche, ein Verhalten.
  const [searchOpen, setSearchOpen] = useState(false);
  // Globale Tastenkombination ⌘K / Strg+K oeffnet die Suche.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  // Punkt 115 (Paket 1): Seiten oeffnen die Suche mit Startbegriff (`oeffneGlobaleSuche`).
  const [searchStart, setSearchStart] = useState("");
  useEffect(() => {
    const onOeffnen = (e: Event) => {
      setSearchStart((e as CustomEvent<string>).detail ?? "");
      setSearchOpen(true);
    };
    window.addEventListener(GLOBALE_SUCHE_EREIGNIS, onOeffnen);
    return () => window.removeEventListener(GLOBALE_SUCHE_EREIGNIS, onOeffnen);
  }, []);

  // MAESTRO 2.0 (A2b): vom Copilot angefordertes Navigationsziel im Router ausführen — AppShell
  // liegt INNERHALB des Routers, kann also useNavigate nutzen. Nach dem Sprung sofort auf null
  // zurücksetzen, damit kein zweiter (Doppel-)Sprung ausgelöst wird.
  useEffect(() => {
    if (!navigateTarget) return;
    navigate(navigateTarget);
    requestNavigate(null);
  }, [navigateTarget, navigate, requestNavigate]);

  const closeAccount = useCallback(() => setAccountOpen(false), []);
  const closeSwitch = useCallback(() => setSwitchOpen(false), []);
  const accountRef = useDismiss<HTMLDivElement>(accountOpen, closeAccount);
  const switchRef = useDismiss<HTMLDivElement>(switchOpen, closeSwitch);

  // M-27: Membership passend zur aktuellen Subdomain (Multi-Tenant-Nutzer sahen sonst Name/Rolle
  // eines zufaellig ersten Tenants).
  const membership = currentMembership(me);
  const tenantName = membership?.name ?? window.location.hostname.split(".")[0];
  const role = membership?.role;
  const roleLabel = role ? (ROLE_LABEL[role] ?? role) : undefined;
  const memberships = me?.memberships ?? [];
  const canSwitch = memberships.length > 1;

  const personName = user?.displayName?.trim() || user?.primaryEmail || "Konto";
  const personInitials = initials(user?.displayName, user?.primaryEmail);

  // Betrieb wechseln = auf die Subdomain des Ziel-Tenants springen (Host ist die Tenant-Quelle).
  function switchTenant(slug: string) {
    const parts = window.location.hostname.split(".");
    if (parts.length < 2 || parts[0] === slug) return; // lokale Dev-Hosts ohne Subdomain: kein Sprung
    parts[0] = slug;
    const port = window.location.port ? `:${window.location.port}` : "";
    window.location.assign(`${window.location.protocol}//${parts.join(".")}${port}/`);
  }

  // Phase H1: Breiten-/Rand-Übergänge sind normalerweise motion-safe animiert (Einklappen, Copilot
  // auf/zu), werden aber WÄHREND eines aktiven Zugs entfernt, damit der Griff 1:1 dem Zeiger folgt.
  const marginTransition = dragging ? "" : "motion-safe:transition-[margin] motion-safe:duration-200";
  const widthTransition = dragging ? "" : "motion-safe:transition-[width] motion-safe:duration-200";

  return (
    <div className="min-h-dvh bg-surface text-[13px] text-on-background dark:bg-night-canvas dark:text-inverse-on-surface">
      {/* Impersonation-Banner (P3, docs/KONZEPT-ADMIN-PANEL.md § Impersonation Punkt 6): rendert
          sich selbst zu nichts, solange kein aktiver Grant bestaetigt ist, und schreibt seine
          gemessene Hoehe in `--impersonation-banner-h` (Default 0px) - Header/Main unten
          verschieben sich um genau diesen Wert nach unten, damit ohne aktives Banner exakt das
          bisherige Layout entsteht. */}
      <ImpersonationBanner />
      {/* SideNavBar (Desktop) — Dunkelbraune Sidebar (#4A2511), Breite CSS-variabel (`--nav-w`).
          Eingeklappt = schmale Icon-Rail (~64px). */}
      <nav
        className={`fixed left-0 top-0 z-40 hidden h-screen flex-col border-r border-white/[0.06] bg-[#4A2511] py-[18px] text-inverse-on-surface lg:flex lg:w-[var(--nav-w)] ${
          collapsed ? "px-2.5" : "px-3.5"
        } ${widthTransition}`}
      >
        {/* Wortmarke + Einklapp-Umschalter. Eingeklappt stapelt sich Logo über dem Ausklapp-Chevron. */}
        {collapsed ? (
          <div className="flex flex-col items-center gap-2 px-1 pb-5 pt-1">
            <Logo size={26} />
            <button
              type="button"
              onClick={toggleCollapsed}
              title="Menü ausklappen"
              aria-label="Menü ausklappen"
              className="rounded-lg p-1 text-inverse-on-surface/70 transition-colors hover:bg-white/10 hover:text-inverse-on-surface"
            >
              <Icon name="chevron_right" className="text-[20px]" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2.5 px-2 pb-5 pt-1">
            <Logo
              size={26}
              mitWortmarke
              wortmarkeClassName="flex-1 font-label-md text-[13px] font-bold tracking-[0.22em] text-inverse-on-surface"
            />
            <button
              type="button"
              onClick={toggleCollapsed}
              title="Menü einklappen"
              aria-label="Menü einklappen"
              className="rounded-lg p-1 text-inverse-on-surface/70 transition-colors hover:bg-white/10 hover:text-inverse-on-surface"
            >
              <Icon name="chevron_left" className="text-[20px]" />
            </button>
          </div>
        )}
        <div className={`mb-4 ${collapsed ? "px-0" : "px-1"}`}>
          <Button
            className={`w-full ${collapsed ? "!px-0" : ""}`}
            onClick={() => navigate("/anfragen?neu=1")}
            title={collapsed ? "Neue Anfrage" : undefined}
            aria-label="Neue Anfrage"
          >
            <Icon name="add" className="text-[18px]" /> {!collapsed && "Neue Anfrage"}
          </Button>
        </div>
        <SeitenNavigation collapsed={collapsed} wartetAufDich={wartetAufDich} posteingangOhneVorgang={posteingangOhneVorgang} gedaechtnisNeu={gedaechtnisNeu} />

        {/* Konto-Bereich (10a): Betrieb-Karte oben, Person-Karte darunter öffnet das Menü nach oben.
            Eingeklappt schrumpfen beide auf ihre Avatar-Buttons; die Popups öffnen breiter nach rechts. */}
        <div className="mt-auto flex flex-col gap-2.5 pt-4">
          {/* Betrieb-Umschalter */}
          <div className="relative" ref={switchRef}>
            {canSwitch && switchOpen && (
              <div
                className={`absolute bottom-full z-50 mb-2 overflow-hidden rounded-xl border border-outline-variant/30 bg-surface-container-lowest text-on-background shadow-2xl dark:border-white/10 dark:bg-night-card dark:text-inverse-on-surface ${
                  // Popup NIE an die (evtl. schmal gezogene) Menübreite koppeln: min-w erzwingt eine
                  // nutzbare Breite und lässt das Menü nach rechts überlaufen (wie beim Icon-Menü),
                  // statt am schmalen Rand abgeschnitten zu werden. z-50 + kein clippender Vorfahr.
                  collapsed ? "left-0 w-[240px]" : "left-0 right-0 min-w-[240px]"
                }`}
              >
                <div className="px-3 py-2 font-body-sm text-[10.5px] font-semibold uppercase tracking-wide text-on-surface-variant dark:text-surface-variant">
                  Betrieb wechseln
                </div>
                <div className="max-h-[40vh] overflow-y-auto p-1.5 pt-0">
                  {memberships.map((m) => {
                    const active = m.slug === membership?.slug;
                    return (
                      <button
                        key={m.tenantId}
                        type="button"
                        onClick={() => (active ? closeSwitch() : switchTenant(m.slug))}
                        className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-surface-container-high dark:hover:bg-white/5"
                      >
                        <Logo
                          size={28}
                          variant="soft"
                          textClassName="text-[12px]"
                          buchstabe={(m.name ?? "?").slice(0, 1).toUpperCase()}
                        />
                        <span className="flex-1 truncate font-body-sm text-body-sm font-medium">{m.name}</span>
                        {active && <Icon name="check" className="text-[18px] text-primary" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {collapsed ? (
              <button
                type="button"
                disabled={!canSwitch}
                onClick={() => {
                  setAccountOpen(false);
                  setSwitchOpen((v) => !v);
                }}
                title={canSwitch ? "Betrieb wechseln" : tenantName}
                className={[
                  "mx-auto flex h-9 w-9 items-center justify-center rounded-[10px] border border-white/[0.12] bg-white/5 transition-colors",
                  canSwitch ? "hover:bg-white/[0.09]" : "cursor-default",
                ].join(" ")}
              >
                <span className="grid h-6 w-6 place-items-center rounded-[7px] bg-white/[0.12] text-[11px] font-bold text-inverse-on-surface">
                  {(tenantName || "?").slice(0, 1).toUpperCase()}
                </span>
              </button>
            ) : (
              <button
                type="button"
                disabled={!canSwitch}
                onClick={() => {
                  setAccountOpen(false);
                  setSwitchOpen((v) => !v);
                }}
                className={[
                  "flex w-full items-center gap-2.5 rounded-[10px] border border-white/[0.12] bg-white/5 px-2.5 py-2 text-left transition-colors",
                  canSwitch ? "hover:bg-white/[0.09]" : "cursor-default",
                ].join(" ")}
              >
                <div className="grid h-6 w-6 shrink-0 place-items-center rounded-[7px] bg-white/[0.12] text-[11px] font-bold text-inverse-on-surface">
                  {(tenantName || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-body-sm text-[12px] font-semibold text-inverse-on-surface">{tenantName}</p>
                  <p className="truncate font-body-sm text-[10.5px] text-inverse-on-surface/72">
                    {canSwitch ? "Betrieb wechseln" : (roleLabel ?? "Betrieb")}
                  </p>
                </div>
                {canSwitch && <Icon name="unfold_more" className="text-[16px] text-inverse-on-surface/72" />}
              </button>
            )}
          </div>

          {/* Person-Karte → Konto-Menü */}
          <div className="relative" ref={accountRef}>
            {accountOpen && (
              <div
                role="menu"
                className={`absolute bottom-full z-50 mb-2 max-h-[72vh] overflow-y-auto rounded-xl border border-outline-variant/30 bg-surface-container-lowest text-on-background shadow-2xl dark:border-white/10 dark:bg-night-card dark:text-inverse-on-surface ${
                  // Konto-Menü nie an die (evtl. schmal gezogene) Menübreite koppeln: min-w erzwingt
                  // eine nutzbare Breite und lässt das Popup nach rechts überlaufen (wie beim Icon-Menü),
                  // statt am schmalen Rand abgeschnitten zu werden. z-50 + kein clippender Vorfahr.
                  collapsed ? "left-0 w-[264px]" : "left-0 right-0 min-w-[264px]"
                }`}
              >
                {/* Profil-Header */}
                <div className="flex items-center gap-3 border-b border-outline-variant/20 px-3.5 py-3 dark:border-white/10">
                  <Logo
                    size={36}
                    radiusClassName="rounded-full"
                    variant="soft"
                    textClassName="text-[13px]"
                    buchstabe={personInitials}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-body-sm text-[13.5px] font-semibold">{personName}</span>
                      {roleLabel && (
                        <span className="shrink-0 rounded-full bg-surface-container-high px-2 py-0.5 text-[10px] font-semibold text-on-surface-variant dark:bg-white/10 dark:text-surface-variant">
                          {roleLabel}
                        </span>
                      )}
                    </div>
                    {user?.primaryEmail && (
                      <div className="truncate font-body-sm text-[11.5px] text-on-surface-variant dark:text-surface-variant">
                        {user.primaryEmail}
                      </div>
                    )}
                  </div>
                </div>

                {/* Profil / Benachrichtigungen */}
                <div className="p-1.5">
                  <MenuItem
                    icon="person"
                    label="Mein Profil"
                    onClick={() => {
                      closeAccount();
                      navigate("/mein-konto");
                    }}
                  />
                  <MenuItem
                    icon="notifications"
                    label="Benachrichtigungen"
                    onClick={() => {
                      closeAccount();
                      navigate("/mein-konto?tab=benachrichtigungen");
                    }}
                  />
                </div>

                {/* Darstellung: Hell / Dunkel / System */}
                <div className="flex items-center gap-2 border-t border-outline-variant/20 px-3.5 py-2.5 dark:border-white/10">
                  <span className="flex-1 font-body-sm text-body-sm font-medium">Darstellung</span>
                  <div className="flex gap-0.5 rounded-lg bg-surface-container-high p-0.5 dark:bg-white/5">
                    {THEME_OPTS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        title={opt.label}
                        aria-pressed={theme === opt.value}
                        onClick={() => setTheme(opt.value)}
                        className={[
                          "grid h-[26px] w-8 place-items-center rounded-md transition-colors",
                          theme === opt.value
                            ? "bg-surface-container-lowest text-primary shadow-sm dark:bg-night-card"
                            : "text-on-surface-variant hover:text-on-background dark:text-surface-variant dark:hover:text-inverse-on-surface",
                        ].join(" ")}
                      >
                        <Icon name={opt.icon} className="text-[16px]" />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Betriebs-Ebene + Hilfe */}
                <div className="border-t border-outline-variant/20 p-1.5 dark:border-white/10">
                  <MenuItem
                    icon="settings"
                    label="Einstellungen"
                    hint="Betrieb"
                    onClick={() => {
                      closeAccount();
                      navigate("/einstellungen");
                    }}
                  />
                  <MenuItem
                    icon="help"
                    label="Hilfe & Support"
                    onClick={() => {
                      closeAccount();
                      setHelpOpen(true);
                    }}
                  />
                </div>

                {/* Abmelden */}
                <div className="border-t border-outline-variant/20 p-1.5 dark:border-white/10">
                  <MenuItem
                    icon="logout"
                    label="Abmelden"
                    danger
                    onClick={() => {
                      closeAccount();
                      void user?.signOut();
                    }}
                  />
                </div>
              </div>
            )}
            {collapsed ? (
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={accountOpen}
                onClick={() => {
                  setSwitchOpen(false);
                  setAccountOpen((v) => !v);
                }}
                title={personName}
                className={[
                  "mx-auto grid h-9 w-9 place-items-center rounded-full text-[10.5px] font-bold text-inverse-on-surface transition-colors",
                  accountOpen ? "bg-white/20 outline outline-1 outline-white/[0.14]" : "bg-white/[0.16] hover:bg-white/[0.22]",
                ].join(" ")}
              >
                {personInitials}
              </button>
            ) : (
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={accountOpen}
                onClick={() => {
                  setSwitchOpen(false);
                  setAccountOpen((v) => !v);
                }}
                className={[
                  "flex w-full items-center gap-2.5 rounded-[9px] px-2 py-1.5 text-left transition-colors",
                  accountOpen ? "bg-white/10 outline outline-1 outline-white/[0.14]" : "hover:bg-white/[0.07]",
                ].join(" ")}
              >
                <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-white/[0.16] text-[10.5px] font-bold text-inverse-on-surface">
                  {personInitials}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-body-sm text-[12px] font-semibold text-inverse-on-surface">{personName}</p>
                  <p className="truncate font-body-sm text-[10.5px] text-inverse-on-surface/72">{roleLabel ?? "Konto"}</p>
                </div>
                <Icon
                  name="expand_less"
                  className={`text-[16px] text-inverse-on-surface/72 transition-transform ${accountOpen ? "" : "rotate-180"}`}
                />
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Ziehgriff Menü ↔ Inhalt (nur ab lg). Auch eingeklappt gerendert (an der Rail-Kante) -
          so laesst sich das Menü per Ziehen wieder aufklappen, nicht nur ueber den
          Chevron-Button. Doppelklick klappt eingeklappt aus statt die Breite zurueckzusetzen. */}
      <Resizer
        ariaLabel={collapsed ? "Menü ausklappen" : "Menübreite anpassen"}
        valueNow={collapsed ? NAV_RAIL : navW}
        valueMin={NAV_RAIL}
        valueMax={NAV_MAX}
        positionStyle={{ left: "var(--nav-w)" }}
        edge="left"
        onPointerDown={startNavDrag}
        onKeyDown={onNavKey}
        onDoubleClick={collapsed ? toggleCollapsed : resetNav}
      />

      {/* TopNavBar — Design 10a: Seitentitel links, zentrierte Suche mit ⌘K, rechts NUR die Glocke.
          (Mobile: zusätzlich Hell/Dunkel-Schalter + Abmelden, da die Sidebar dort versteckt ist.)
          Rand folgt `--nav-w` — der Copilot schwebt seit 28.08.2026 frei ueber dem Inhalt statt
          ihn zu verdraengen, braucht also keinen reservierten Rand mehr. */}
      <header
        className={`sticky top-[var(--impersonation-banner-h,0px)] z-30 mt-[var(--impersonation-banner-h,0px)] flex h-topbar-height items-center gap-1 border-b border-[#EAE3D5] bg-[#FFFDF8] px-4 dark:border-white/[0.08] dark:bg-night-bar md:gap-3 md:px-margin-desktop lg:ml-[var(--nav-w)] ${marginTransition} ${pinnedOpen ? "lg:mr-[380px]" : ""}`}
      >
        {/* Links: mobil Hamburger (öffnet `MobileNavDrawer`, alle 17 Ziele) + Wortmarke (Sidebar
            versteckt), ab lg der aktive Seitentitel. Nav-Umbau 28.08.2026: der Knopf sitzt in der
            Topbar statt als sechstes Icon in der Fußleiste — dort ist bei 390px kein Platz mehr
            (s. Kommentar in NavListe.tsx). */}
        <div className="flex items-center gap-1 lg:w-56">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Hauptnavigation öffnen"
            className="-ml-1 rounded-full p-2 text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-primary dark:text-surface-variant dark:hover:bg-white/10 lg:hidden"
          >
            <Icon name="menu" className="text-xl" />
          </button>
          <span className="font-headline-sm text-headline-sm font-bold text-primary lg:hidden">MAESTRO</span>
          <h1 className="hidden truncate text-[15px] font-bold tracking-[-0.01em] text-on-background dark:text-inverse-on-surface lg:block">
            {activeLabel}
          </h1>
        </div>

        {/* Mitte: zentrierte Suche mit ⌘K-Chip (Design: max 420px, bg #F6F1E7, radius 9) */}
        <div className="hidden flex-1 justify-center md:flex">
          <div className="group relative w-full max-w-[420px]">
            <Icon
              name="search"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[18px] text-on-surface-variant transition-colors group-focus-within:text-primary dark:text-surface-variant"
            />
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="w-full rounded-[9px] border border-transparent bg-[#F6F1E7] py-2 pl-9 pr-14 text-left text-[13px] text-on-surface-variant/70 outline-none transition hover:border-primary/30 focus:border-primary/40 dark:bg-white/5 dark:text-surface-variant/60"
            >
              Suchen …
            </button>
            <kbd className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 items-center rounded-md border border-outline-variant/60 bg-surface-container-lowest px-1.5 py-0.5 font-sans text-[10.5px] font-semibold text-on-surface-variant lg:inline-flex dark:border-white/10 dark:bg-white/5 dark:text-surface-variant">
              ⌘K
            </kbd>
          </div>
        </div>

        {/* Rechts: Glocke (immer). Auf Mobile zusätzlich Theme-Schalter + Abmelden. */}
        <div className="flex items-center gap-0 md:gap-1.5 lg:w-56 lg:justify-end">
          {/* Handy (unter md): Suchfeld in der Mitte ist versteckt, darum hier als Icon. */}
          <button
            type="button"
            aria-label="Suchen"
            title="Suchen"
            onClick={() => setSearchOpen(true)}
            className="rounded-full p-2 text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-primary dark:text-surface-variant dark:hover:bg-white/10 md:hidden"
          >
            <Icon name="search" className="text-xl" />
          </button>
          {/* Hell/Dunkel-Schalter: unter lg (dort ist die Sidebar versteckt und das Konto-Menue mit
              „Darstellung“ nicht im Blick). Seit 27.10.2026 auch auf dem Handy — Antoine: „oben fehlt das
              Symbol fuer Mond und Tag“. 44 px Tippflaeche (Handy-Mindestmass), `aria-label` nennt das Ziel. */}
          <button
            type="button"
            data-testid="theme-schalter"
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-primary dark:hover:bg-white/10 lg:hidden"
            title={resolvedDark ? "Heller Modus" : "Dunkler Modus"}
            aria-label={resolvedDark ? "Zum hellen Modus wechseln" : "Zum dunklen Modus wechseln"}
            onClick={() => setTheme(resolvedDark ? "light" : "dark")}
          >
            <Icon name={resolvedDark ? "light_mode" : "dark_mode"} className="text-xl" />
          </button>
          {/* MAESTRO Copilot (2.0): angedockte KI-Chat-Spalte rechts ein-/ausklappen. */}
          <button
            type="button"
            onClick={toggleCopilot}
            aria-pressed={copilotOpen}
            title="MAESTRO KI-Assistent"
            aria-label="MAESTRO KI-Assistent ein- oder ausblenden"
            className={[
              "rounded-full p-2 transition-colors hover:bg-surface-container-high dark:hover:bg-white/10",
              copilotOpen ? "text-primary" : "text-on-surface-variant hover:text-primary dark:text-surface-variant",
            ].join(" ")}
          >
            <Icon name="smart_toy" className="text-xl" />
          </button>
          {/* Automatik P4.3: Läuft im Hintergrund eine Referenz-Analyse, ist das auf JEDER Seite
              sichtbar — der Indikator hängt in der Topbar der AppShell, nicht auf einer Seite. */}
          <ReferenzLaufIndikator />
          {/* Merge 2026-07-18: EINE Glocke — der Aktivitäts-Drawer aus main (#18), erweitert
              um die „Braucht Aufmerksamkeit"-Sektion aus dem CX-Audit (jede Meldung klickbar). */}
          <NotificationsBell />
          {/* Undo-Loop Einheit C, Schritt 9: globaler persönlicher „Rückgängig"-Stack, exakt
              nach demselben Icon-Knopf-mit-Badge-Muster wie die Glocke direkt daneben. */}
          <UndoStack />
          <button
            type="button"
            className="rounded-full p-2 text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-primary dark:text-surface-variant dark:hover:bg-white/10 lg:hidden"
            title="Abmelden"
            onClick={() => void user?.signOut()}
          >
            <Icon name="logout" className="text-xl" />
          </button>
        </div>
      </header>

      {/* Main Content Canvas — auf lg gegen das Menü (`--nav-w`) eingerueckt; der Copilot schwebt
          im Float-/Andock-Modus frei darueber (siehe Kommentar an der TopNavBar oben), reserviert
          im "pinned"-Modus aber echten Platz rechts (380px, deckungsgleich mit der Spaltenbreite
          in CopilotPanel.tsx — Tailwind braucht dafuer ein statisches Literal, keine JS-Variable). */}
      {/* Zentrieren (Befund Vorgangsseite „Salon“, 26.09.2026): `lg:ml-[var(--nav-w)]` hob das
          `mx-auto` am `<main>` auf — über 1680 px klebte der Inhalt links und rechts blieb ein
          breiter Streifen leer. Jetzt nimmt `<main>` die volle Breite neben dem Menü, und die
          innere Spalte (max. 1440 px) steht darin mittig. */}
      <main
        className={`mt-[var(--impersonation-banner-h,0px)] min-h-[calc(100dvh-58px)] px-4 pb-24 pt-6 md:px-margin-desktop lg:ml-[var(--nav-w)] lg:pb-10 ${marginTransition} ${pinnedOpen ? "lg:mr-[380px]" : ""}`}
      >
        <div className="mx-auto w-full max-w-container-max">
          {/* Fehler-Ansicht nur im Inhaltsbereich (28.09.2026): Seitenleiste und Kopf bleiben
              bedienbar; `key` setzt die Grenze beim Seitenwechsel zurück. */}
          <AppErrorBoundary bereich="inhalt" key={pathname}>
            {children}
          </AppErrorBoundary>
        </div>
      </main>

      {/* Mobile Bottom-Nav (dunkler Karten-Ton, iOS-Safe-Area) — siehe ui/NavListe.tsx */}
      <MobileNavigation wartetAufDich={wartetAufDich} />
      <MobileNavDrawer
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        wartetAufDich={wartetAufDich}
        posteingangOhneVorgang={posteingangOhneVorgang}
        gedaechtnisNeu={gedaechtnisNeu}
      />

      {/* Hilfe & Support (Design 11d) — Panel von rechts, aus dem Konto-Menü von überall erreichbar */}
      <HelpPanel open={helpOpen} onClose={() => setHelpOpen(false)} />
      <GlobalSearch
        open={searchOpen}
        startBegriff={searchStart}
        onClose={() => {
          setSearchOpen(false);
          setSearchStart("");
        }}
      />

      {/* MAESTRO Copilot (2.0) — global angedockte KI-Chat-Spalte, auf lg fest, darunter Drawer. */}
      <CopilotPanel />
    </div>
  );
}
